import { ref } from 'vue';

export type UseDragOverZoneOptions = {
  /** When provided, only drags that pass this check activate the drop zone highlight. */
  acceptsDrag?: (event: DragEvent) => boolean;
};

// Todo: we should use this generic composable for drag&drop of materials and folders as well.

/**
 * Tracks drag-over state for a drop zone using a nested enter/leave counter,
 * so child elements do not flicker the highlight on dragleave.
 */
export function useDragOverZone(options: UseDragOverZoneOptions = {}) {
  const isDragOver = ref(false);
  const dragOverCounter = ref(0);

  function acceptsDrag(event: DragEvent): boolean {
    return options.acceptsDrag?.(event) ?? true;
  }

  function reset(): void {
    dragOverCounter.value = 0;
    isDragOver.value = false;
  }

  function onDragEnter(event: DragEvent): void {
    if (!acceptsDrag(event)) {
      return;
    }

    event.preventDefault();
    dragOverCounter.value += 1;
    isDragOver.value = true;
  }

  function onDragLeave(event: DragEvent): void {
    if (dragOverCounter.value === 0) {
      return;
    }

    event.preventDefault();
    dragOverCounter.value -= 1;

    if (dragOverCounter.value <= 0) {
      reset();
    }
  }

  function onDragOver(event: DragEvent): void {
    if (!acceptsDrag(event)) {
      return;
    }

    event.preventDefault();
  }

  function onDragEnd(event: DragEvent): void {
    event.preventDefault();
    reset();
  }

  return {
    isDragOver,
    dragOverCounter,
    reset,
    dragHandlers: {
      onDragenter: onDragEnter,
      onDragleave: onDragLeave,
      onDragover: onDragOver,
      onDragend: onDragEnd,
    },
  };
}
